import React, { useEffect, useState, useCallback } from "react";
import { useNotify } from "react-admin";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import { DsBadge, ReadinessBadge, StateBadge, ImpactText } from "../badges";
import { BUCKET_OF, criticalFor, methColor, invalidReasonFor, READY_GREEN, ASSUM_ORANGE, BLOCKED_RED, REVIEW_PURPLE } from "../methodologyUtils";
import { impactLabel } from "../labels";
import ArticleTraceView from "./ArticleTraceView";

// ── Article gap side panel (Fase 1) ──────────────────────────────────────────
// Entity-centric drilldown: click an article → see, per entity in its supply
// chain (article / materials / suppliers / packaging), what it HAS, what's
// MISSING and what's ASSUMED — with the requirement context and inline actions.
// Data source: GET /articles/:id/gaps (one call carries the whole chain because
// the gap ledger is article-centric; grouped client-side by entity).

// A gap status is "satisfied" (does not block) only when provided or assumed.
const SATISFIED = ["RESOLVED_PROVIDED", "RESOLVED_ASSUMPTION"];
const isSatisfied = (s) => SATISFIED.includes(s);

// Bucket used only for grouping/sorting inside a node; the visible status label comes from
// the central StateBadge (spec §3), so there is no local status→word table anymore.
const bucketOf = (status) => BUCKET_OF[status] || "faltan";

// Methodologies a gap currently blocks: required (M/M-A) for a contracted
// methodology AND not yet satisfied. Empty when satisfied or only recommended.
const blocksMethodologies = (gap, contracted) => {
  if (isSatisfied(gap.status)) return [];
  const req = criticalFor(gap.fieldDefinition || {});
  return req.filter((m) => !contracted || contracted.length === 0 || contracted.includes(m));
};

// Which supply-chain node a gap belongs to, by its entityTable.
const nodeOf = (gap) => {
  const t = gap.entityTable || "";
  const name = gap.entityName || (gap.entityId ? gap.entityId.slice(0, 8) : "—");
  if (t === "app_article") return { type: "article", key: "article", label: "Artículo" };
  if (t.startsWith("app_material")) return { type: "material", key: `material:${name}`, label: name };
  if (t === "app_supplier") return { type: "supplier", key: `supplier:${name}`, label: name };
  if (t === "app_retail_packaging") return { type: "packaging", key: `packaging:${name}`, label: name };
  return { type: "other", key: `other:${name}`, label: name };
};

const NODE_ORDER = { article: 0, material: 1, packaging: 2, supplier: 3, other: 4 };
const NODE_LABEL = { article: "artículo", material: "material", packaging: "packaging", supplier: "proveedor", other: "otro" };
// Node colours spread across the wheel, avoiding green/orange/red (reserved for the status badges).
const NODE_COLOR = { article: "#2563eb", material: "#db2777", packaging: "#a16207", supplier: "#7c3aed", other: "#64748b" };
// Sort fields inside a node: unsatisfied first (most urgent), assumed, provided last.
const bucketRank = { faltan: 0, asumibles: 1, asumidos: 2, aportados: 3 };

const useStyles = makeStyles((theme) => ({
  drawer: { width: "58vw", minWidth: 720, maxWidth: 1120 },
  inline: { width: "100%" },
  header: { padding: theme.spacing(2), paddingBottom: theme.spacing(1) },
  scores: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 4 },
  chips: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 },
  priority: {
    background: "rgba(220,38,38,0.08)",
    color: BLOCKED_RED,
    fontWeight: 700,
    fontSize: 13,
    borderRadius: 6,
    padding: "6px 10px",
    margin: theme.spacing(0, 2),
  },
  filters: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: theme.spacing(1.5, 2) },
  body: { padding: theme.spacing(0, 2, 2) },
  node: {
    marginTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(1.5),
    borderLeftWidth: 3,
    borderLeftStyle: "solid",
    borderRadius: 2,
  },
  nodeHead: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: theme.spacing(0.5),
    fontWeight: 700,
    fontSize: 14,
  },
  rollup: { fontSize: 11, marginLeft: "auto", fontWeight: 600 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "5px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: 13,
  },
  statusCol: { width: 92, flexShrink: 0 },
  fieldName: { flex: "1 1 auto", minWidth: 0 },
  value: { color: theme.palette.text.secondary, fontSize: 12 },
  raw: { color: "#ea580c", fontWeight: 600, fontSize: 12 },
  actions: { display: "flex", gap: 4, alignItems: "center", flexShrink: 0 },
  btn: { padding: "0 6px", fontSize: 11, minWidth: 0, lineHeight: 1.8, minHeight: 24 },
  provInput: { width: 72 },
  aportadosToggle: { fontSize: 12, color: "#059669", cursor: "pointer", padding: "4px 0", fontWeight: 600 },
  complete: { color: "#059669", fontSize: 12, fontWeight: 600 },
}));

// One field row: token + name + value/raw + blocking badge + inline actions.
const FieldRow = ({ gap, contracted, onProvide, onReject, onAssume }) => {
  const classes = useStyles();
  const [val, setVal] = useState("");
  const fd = gap.fieldDefinition || {};
  const blocks = blocksMethodologies(gap, contracted);
  const assumed = gap.status === "RESOLVED_ASSUMPTION";
  const provided = gap.status === "RESOLVED_PROVIDED";
  // A pending assumption ("Puedes asumir") can be applied in place — BCome resolves the value.
  const assumable = gap.status === "ASSUMPTION_PENDING";

  return (
    <div className={classes.row}>
      <span className={classes.statusCol}>
        <StateBadge status={gap.status} />
      </span>
      <span className={classes.fieldName}>
        {fd.displayName || fd.fieldKey || "—"}
        {gap.status === "UNRESOLVED_MAPPING" && gap.rawValue && (
          <span className={classes.raw}> · «{gap.rawValue}» sin traducir</span>
        )}
        {(assumed || provided) && gap.resolvedValue && (
          <span className={classes.value}> · {gap.resolvedValue}</span>
        )}
        {gap.status === "REQUESTED" && <span className={classes.value}> · marcado como pedido</span>}
        {gap.status === "INVALID_VALUE" && (
          <span style={{ display: "block", fontSize: 11, color: REVIEW_PURPLE, marginTop: 2 }}>
            {invalidReasonFor(fd.fieldKey)}
          </span>
        )}
      </span>

      {blocks.length > 0 && <ImpactText label={impactLabel(blocks)} />}

      <span className={classes.actions}>
        {assumed ? (
          <Button size="small" variant="outlined" className={classes.btn} onClick={() => onReject(gap)}>
            Rechazar
          </Button>
        ) : !provided ? (
          <>
            {assumable && (
              <Button size="small" variant="contained" color="primary" className={classes.btn} onClick={() => onAssume(gap)}>
                Asumir
              </Button>
            )}
            <TextField
              className={classes.provInput}
              size="small"
              placeholder="valor"
              value={val}
              onChange={(e) => setVal(e.target.value)}
            />
            <Button size="small" variant={assumable ? "outlined" : "contained"} color="primary" className={classes.btn} onClick={() => onProvide(gap, val)}>
              Aportar
            </Button>
          </>
        ) : null}
      </span>
    </div>
  );
};

// `inline` renders the panel content without its own Drawer, so a host (e.g. FieldDrillPanel)
// can swap it into a SINGLE drawer instead of stacking one on top of another (spec §7: no
// drawer sobre drawer). `backLabel` customises the dismiss control when inline.
const ArticleGapPanel = ({ api, articleId, articleName, contracted, initialTab, onClose, onChanged, inline, backLabel }) => {
  const classes = useStyles();
  const notify = useNotify();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(initialTab || "gaps"); // gaps (Pendientes) | trace
  const [estado, setEstado] = useState("todo"); // todo | falta | asumido
  const [methFilter, setMethFilter] = useState("");
  const [blockingOnly, setBlockingOnly] = useState(false);
  const [expanded, setExpanded] = useState({}); // nodeKey → show aportados

  const load = useCallback(async () => {
    if (!articleId) return;
    setLoading(true);
    const r = await api("GET", `/articles/${articleId}/gaps`);
    setLoading(false);
    setData(r.ok ? r.data : null);
    if (!r.ok) notify(`Error ${r.status} cargando la ficha`, "error");
  }, [api, articleId, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (gap, body, okMsg) => {
    const r = await api("PATCH", `/gaps/${gap.id}`, body);
    notify(r.ok ? okMsg : `Error ${r.status}`, r.ok ? "success" : "error");
    if (r.ok) {
      await load();
      if (onChanged) onChanged();
    }
  };
  const provide = (gap, value) => {
    const v = (value || "").trim();
    if (!v) return notify("Introduce un valor antes de aportar", "warning");
    patch(gap, { status: "RESOLVED_PROVIDED", resolvedByActor: "BRAND", resolvedValue: v }, "Dato aportado");
  };
  // Apply the BCome assumption for this single gap: the backend resolves the value write-through.
  const assume = (gap) => patch(gap, { status: "RESOLVED_ASSUMPTION", resolvedByActor: "BCOME" }, "Valor asumido aplicado");
  const reject = (gap) => {
    if (!window.confirm("¿Rechazar la assumption? El gap volverá a contar como falta hasta que llegue el dato real.")) return;
    patch(gap, { status: "ASSUMPTION_REJECTED", resolvedByActor: "BRAND" }, "Assumption rechazada");
  };

  // Filter + group the gaps into supply-chain nodes.
  const gaps = (data?.gaps || []).filter((g) => {
    if (methFilter && !criticalFor(g.fieldDefinition || {}).includes(methFilter)) return false;
    if (blockingOnly && blocksMethodologies(g, contracted).length === 0) return false;
    if (estado === "falta" && (isSatisfied(g.status) || bucketOf(g.status) === "asumidos")) return false;
    if (estado === "asumido" && bucketOf(g.status) !== "asumidos") return false;
    return true;
  });

  const nodesMap = new Map();
  gaps.forEach((g) => {
    const n = nodeOf(g);
    if (!nodesMap.has(n.key)) nodesMap.set(n.key, { ...n, gaps: [], context: g.entityContext || null });
    nodesMap.get(n.key).gaps.push(g);
  });
  const nodes = [...nodesMap.values()].sort(
    (a, b) => NODE_ORDER[a.type] - NODE_ORDER[b.type] || a.label.localeCompare(b.label)
  );

  const blockingCount = (data?.gaps || []).filter((g) => blocksMethodologies(g, contracted).length > 0).length;
  const pendientesCount = (data?.gaps || []).filter((g) => !isSatisfied(g.status)).length;

  const methChips = (data?.methodologies || []).filter(
    (m) => !contracted || contracted.length === 0 || contracted.includes(m.methodology)
  );

  const body = (
    <>
      <Box className={classes.header}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Typography variant="h6" style={{ lineHeight: 1.2 }}>
            {data?.articleName || articleName || (articleId ? articleId.slice(0, 8) : "Artículo")}
          </Typography>
          {inline ? (
            <Button size="small" onClick={onClose}>
              {backLabel || "← Volver"}
            </Button>
          ) : (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {/* Nivel 1 — Estado: readiness + scores + nº de pendientes */}
        <div className={classes.scores}>
          {data?.readinessStatus && (
            <span style={{ marginRight: 8 }}>
              <ReadinessBadge status={data.readinessStatus} />
            </span>
          )}
          L1 {data?.traceabilityL1 ?? "—"}% · L2 {data?.traceabilityL2 ?? "—"}% · Brand data{" "}
          {data?.brandDataRate ?? "—"}%
          {" · "}
          <strong>{pendientesCount} pendiente{pendientesCount === 1 ? "" : "s"}</strong>
        </div>
        <div className={classes.chips}>
          {methChips.map((m) => (
            <DsBadge
              key={m.methodology}
              label={`${m.methodology} ${m.completeness != null ? m.completeness + "%" : ""}`}
              color={methColor(m.readinessStatus)}
            />
          ))}
        </div>
      </Box>

      <Box display="flex" style={{ gap: 8, padding: "0 16px 8px" }}>
        <Button size="small" variant={tab === "gaps" ? "contained" : "outlined"} color="primary" onClick={() => setTab("gaps")}>
          Pendientes
        </Button>
        <Button size="small" variant={tab === "trace" ? "contained" : "outlined"} color="primary" onClick={() => setTab("trace")}>
          Trazabilidad
        </Button>
      </Box>

      {tab === "trace" && <ArticleTraceView api={api} articleId={articleId} />}

      {tab === "gaps" && (
        <>
          {blockingCount > 0 && (
            <div className={classes.priority}>⚠ {blockingCount} campo{blockingCount !== 1 ? "s" : ""} bloquea{blockingCount === 1 ? "" : "n"} el cálculo</div>
          )}

      <Box className={classes.filters}>
        <TextField select size="small" label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)} style={{ minWidth: 120 }}>
          <MenuItem value="todo">Todo</MenuItem>
          <MenuItem value="falta">Solo falta</MenuItem>
          <MenuItem value="asumido">Asumido</MenuItem>
        </TextField>
        <TextField select size="small" label="Metodología" value={methFilter} onChange={(e) => setMethFilter(e.target.value)} style={{ minWidth: 140 }}>
          <MenuItem value="">Todas</MenuItem>
          {(contracted && contracted.length ? contracted : ["LCA", "CEI", "EcoScore", "AGEC"]).map((m) => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
        </TextField>
        <Button size="small" variant={blockingOnly ? "contained" : "outlined"} color="primary" onClick={() => setBlockingOnly((v) => !v)}>
          Solo bloqueantes
        </Button>
      </Box>
      <Divider />

      <Box className={classes.body}>
        {loading && <Box textAlign="center" py={3}><CircularProgress size={24} /></Box>}
        {!loading && nodes.length === 0 && (
          <Box py={3} textAlign="center" style={{ color: "#94a3b8" }}>Sin campos para este filtro</Box>
        )}
        {!loading &&
          nodes.map((node) => {
            // Split provided (aportados) to collapse them behind a counter.
            const provided = node.gaps.filter((g) => bucketOf(g.status) === "aportados");
            const actionable = node.gaps
              .filter((g) => bucketOf(g.status) !== "aportados")
              .sort((a, b) => bucketRank[bucketOf(a.status)] - bucketRank[bucketOf(b.status)]);
            const roll = { faltan: 0, asumidos: 0, aportados: 0 };
            node.gaps.forEach((g) => {
              const bk = bucketOf(g.status);
              roll[bk === "asumibles" ? "asumidos" : bk] = (roll[bk === "asumibles" ? "asumidos" : bk] || 0) + 1;
            });
            const showAportados = expanded[node.key];
            return (
              <div key={node.key} className={classes.node} style={{ borderLeftColor: NODE_COLOR[node.type] }}>
                <div className={classes.nodeHead}>
                  <DsBadge label={NODE_LABEL[node.type]} color={NODE_COLOR[node.type]} />
                  <span>{node.label}</span>
                  {node.context && (
                    <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>· {node.context}</span>
                  )}
                  <span className={classes.rollup}>
                    {roll.faltan > 0 && <span style={{ color: BLOCKED_RED }}>{roll.faltan} falta </span>}
                    {roll.asumidos > 0 && <span style={{ color: ASSUM_ORANGE }}>{roll.asumidos} asumido </span>}
                    {roll.aportados > 0 && <span style={{ color: READY_GREEN }}>{roll.aportados} ok</span>}
                  </span>
                </div>

                {actionable.map((g) => (
                  <FieldRow key={g.id} gap={g} contracted={contracted} onProvide={provide} onReject={reject} onAssume={assume} />
                ))}

                {provided.length > 0 && (
                  <>
                    <div className={classes.aportadosToggle} onClick={() => setExpanded((e) => ({ ...e, [node.key]: !e[node.key] }))}>
                      {showAportados ? "▾" : "▸"} {provided.length} aportado{provided.length !== 1 ? "s" : ""}
                    </div>
                    {showAportados &&
                      provided.map((g) => (
                        <FieldRow key={g.id} gap={g} contracted={contracted} onProvide={provide} onReject={reject} onAssume={assume} />
                      ))}
                  </>
                )}
                {actionable.length === 0 && provided.length > 0 && !showAportados && (
                  <div className={classes.complete}>completo</div>
                )}
              </div>
            );
          })}
          </Box>
        </>
      )}
    </>
  );

  if (inline) return <div className={classes.inline}>{body}</div>;
  return (
    <Drawer anchor="right" open onClose={onClose} classes={{ paper: classes.drawer }}>
      {body}
    </Drawer>
  );
};

export default ArticleGapPanel;
